import { useState, useEffect } from 'react'
import { Palette, Settings, MessageSquare, Monitor, Bot, Upload, Trash2, FileText, Eye, EyeOff, Edit2, X, Save, Loader2, Plus, Square, Sparkles, Key, RefreshCw } from 'lucide-react'
import documentService from '../../services/documentService'
import ConfirmationModal from '../popups/confirmationModal/ConfirmationModal'
import ConfirmationInputModal from '../popups/confirmationModal/ConfirmationInputModal'
import AlertModal from '../popups/confirmationModal/AlertModal'
import { generateBotCredentials } from '../../services/bot_configuration'

// Predefined fields (constant outside component)
const predefinedFields = [
  { key: 'full_name', label: 'Full Name', required: false },
  { key: 'email_address', label: 'Email Address', required: false },
  { key: 'phone_number', label: 'Phone Number', required: false },
  { key: 'date_of_birth', label: 'DOB', required: false },
  { key: 'company_name', label: 'Company Name', required: false },
  { key: 'place_name', label: 'Place Name', required: false },
]

const WidgetConfig = ({
  config,
  onConfigChange,
  hasModel = true
}) => {
  const [activeTab, setActiveTab] = useState(hasModel ? 'appearance' : 'bot-config')
  const [documents, setDocuments] = useState([])
  const [isUploading, setIsUploading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(0)
  const [isLoadingDocuments, setIsLoadingDocuments] = useState(false)
  const [showApiKey, setShowApiKey] = useState(false)
  const [isGeneratingCredentials, setIsGeneratingCredentials] = useState(false)

  // Update active tab when hasModel changes
  useEffect(() => {
    if (!hasModel && activeTab !== 'bot-config') {
      setActiveTab('bot-config')
    }
  }, [hasModel, activeTab])

  // Document editor state
  const [isEditorOpen, setIsEditorOpen] = useState(false)
  const [editingDocument, setEditingDocument] = useState(null)
  const [editorContent, setEditorContent] = useState('')
  const [editorDocumentName, setEditorDocumentName] = useState('')
  const [isLoadingContent, setIsLoadingContent] = useState(false)
  const [isSavingContent, setIsSavingContent] = useState(false)

  // Modal state
  const [confirmationModal, setConfirmationModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    type: 'warning',
    onConfirm: () => { },
    confirmText: 'Confirm',
    cancelText: 'Cancel'
  })
  const [regenerateModal, setRegenerateModal] = useState({
    isOpen: false
  })
  const [alertModal, setAlertModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    type: 'info'
  })

  // Custom lead fields state
  const [customFieldInput, setCustomFieldInput] = useState('')
  const [customFields, setCustomFields] = useState([])

  // Extract custom fields from leadFields on mount and when config changes
  useEffect(() => {
    if (config.leadFields) {
      const allFields = config.leadFields.split(',')
      const predefinedKeys = predefinedFields.map(f => f.key)
      const custom = allFields
        .filter(field => field.trim() && !predefinedKeys.includes(field.trim()))
        .map(field => ({
          key: field.trim(),
          label: field.trim().replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
        }))
      setCustomFields(custom)
    } else {
      setCustomFields([])
    }
  }, [config.leadFields])

  // Load documents when component mounts (only if model exists)
  useEffect(() => {
    if (hasModel) {
      loadDocuments()
    }
  }, [hasModel])

  const tabs = [
    { id: 'appearance', label: 'Appearance', icon: Palette },
    { id: 'behavior', label: 'Behavior', icon: Settings },
    { id: 'messages', label: 'Messages', icon: MessageSquare },
    { id: 'bot-config', label: 'Bot Config', icon: Bot }
  ]

  const handleConfigChange = (key, value) => {
    onConfigChange({
      ...config,
      [key]: value
    })
  }

  const handleAddCustomField = () => {
    const fieldKey = customFieldInput.trim().toLowerCase().replace(/\s+/g, '_')

    if (!fieldKey) {
      setAlertModal({
        isOpen: true,
        title: 'Validation Error',
        message: 'Please enter a field name.',
        type: 'warning'
      })
      return
    }

    // Check if field already exists
    const predefinedKeys = predefinedFields.map(f => f.key)
    const existingCustomKeys = customFields.map(f => f.key)

    if (predefinedKeys.includes(fieldKey) || existingCustomKeys.includes(fieldKey)) {
      setAlertModal({
        isOpen: true,
        title: 'Validation Error',
        message: 'This field already exists.',
        type: 'warning'
      })
      return
    }

    // Validate field name (alphanumeric and underscores only)
    if (!/^[a-z0-9_]+$/.test(fieldKey)) {
      setAlertModal({
        isOpen: true,
        title: 'Validation Error',
        message: 'Field name can only contain lowercase letters, numbers, and underscores.',
        type: 'warning'
      })
      return
    }

    // Add custom field
    const newCustomField = {
      key: fieldKey,
      label: fieldKey.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
    }

    const newCustomFields = [...customFields, newCustomField]
    setCustomFields(newCustomFields)

    // Update leadFields to include the new custom field
    const currentFields = config.leadFields ? config.leadFields.split(',').filter(f => f.trim()) : []
    const newFields = [...currentFields, fieldKey]
    handleConfigChange('leadFields', newFields.join(','))

    // Clear input
    setCustomFieldInput('')
  }

  // Generate bot credentials
  const handleGenerateCredentials = async () => {
    try {
      setIsGeneratingCredentials(true)

      // generateBotCredentials already returns response.data
      const credentials = await generateBotCredentials()

      // Update config with new credentials in a single call to avoid state update conflicts
      // The API response contains bot_uuid and bot_secret_key
      if (credentials.bot_uuid && credentials.bot_secret_key) {
        // Update both credentials at once
        onConfigChange({
          ...config,
          botUuid: credentials.bot_uuid,
          botSecretKey: credentials.bot_secret_key
        })

        setAlertModal({
          isOpen: true,
          title: 'Success',
          message: 'Bot credentials generated successfully!',
          type: 'success'
        })
      } else {
        throw new Error('Invalid response format from server')
      }
    } catch (error) {
      console.error('Error generating bot credentials:', error)
      setAlertModal({
        isOpen: true,
        title: 'Generation Failed',
        message: error.response?.data?.detail?.msg || error.response?.data?.msg || error.message || 'Failed to generate bot credentials. Please try again.',
        type: 'danger'
      })
    } finally {
      setIsGeneratingCredentials(false)
    }
  }

  // Regenerate bot credentials (same API, but with confirmation)
  const handleRegenerateCredentials = async () => {
    try {
      setIsGeneratingCredentials(true)

      // generateBotCredentials already returns response.data
      const credentials = await generateBotCredentials()

      // Update config with new credentials in a single call to avoid state update conflicts
      // The API response contains bot_uuid and bot_secret_key
      if (credentials.bot_uuid && credentials.bot_secret_key) {
        // Update both credentials at once
        onConfigChange({
          ...config,
          botUuid: credentials.bot_uuid,
          botSecretKey: credentials.bot_secret_key
        })

        setAlertModal({
          isOpen: true,
          title: 'Success',
          message: 'Bot credentials regenerated successfully!',
          type: 'success'
        })
        window.location.reload();
      } else {
        throw new Error('Invalid response format from server')
      }
    } catch (error) {
      console.error('Error regenerating bot credentials:', error)
      setAlertModal({
        isOpen: true,
        title: 'Regeneration Failed',
        message: error.response?.data?.detail?.msg || error.response?.data?.msg || error.message || 'Failed to regenerate bot credentials. Please try again.',
        type: 'danger'
      })
    } finally {
      setIsGeneratingCredentials(false)
    }
  }

  // Document management functions
  const loadDocuments = async () => {
    try {
      setIsLoadingDocuments(true)
      const docs = await documentService.getDocuments()
      setDocuments(docs)
    } catch (error) {
      console.error('Error loading documents:', error)
    } finally {
      setIsLoadingDocuments(false)
    }
  }

  const handleFileUpload = async (file) => {
    try {
      // Validate file
      documentService.validateFile(file)

      setIsUploading(true)
      setUploadProgress(0)

      // Pass the file directly to preserve the original filename
      const result = await documentService.uploadDocument(file)

      // Reload documents list
      await loadDocuments()

      // Update config with uploaded document info
      handleConfigChange('uploadedDocument', {
        id: result.id,
        name: file.name,
        size: file.size,
        type: file.type
      })

    } catch (error) {
      console.error('Error uploading document:', error)
      setAlertModal({
        isOpen: true,
        title: 'Upload Failed',
        message: error.message || 'Failed to upload document. Please try again.',
        type: 'danger'
      })
    } finally {
      setIsUploading(false)
      setUploadProgress(0)
    }
  }

  const handleDeleteDocument = (documentId) => {
    setConfirmationModal({
      isOpen: true,
      title: 'Delete Document',
      message: 'Are you sure you want to delete this document? This action cannot be undone.',
      type: 'danger',
      confirmText: 'Delete',
      cancelText: 'Cancel',
      onConfirm: async () => {
        try {
          await documentService.deleteDocument(documentId)
          await loadDocuments()

          // Clear uploaded document from config if it was deleted
          if (config.uploadedDocument?.id === documentId) {
            handleConfigChange('uploadedDocument', null)
          }

          setAlertModal({
            isOpen: true,
            title: 'Success',
            message: 'Document deleted successfully.',
            type: 'success'
          })
        } catch (error) {
          console.error('Error deleting document:', error)
          setAlertModal({
            isOpen: true,
            title: 'Delete Failed',
            message: error.message || 'Failed to delete document. Please try again.',
            type: 'danger'
          })
        }
      }
    })
  }

  // Open document editor
  const handleEditDocument = async (documentId) => {
    try {
      setIsLoadingContent(true)
      setIsEditorOpen(true)
      setEditingDocument(documentId)

      const result = await documentService.getDocumentContent(documentId)
      setEditorContent(result.content || '')
      setEditorDocumentName(result.documentName || '')
    } catch (error) {
      console.error('Error loading document content:', error)
      setAlertModal({
        isOpen: true,
        title: 'Load Failed',
        message: error.message || 'Failed to load document content. Please try again.',
        type: 'danger'
      })
      setIsEditorOpen(false)
      setEditingDocument(null)
    } finally {
      setIsLoadingContent(false)
    }
  }

  // Save edited document
  const handleSaveDocument = async () => {
    if (!editingDocument || !editorContent.trim()) {
      setAlertModal({
        isOpen: true,
        title: 'Validation Error',
        message: 'Document content cannot be empty.',
        type: 'warning'
      })
      return
    }

    try {
      setIsSavingContent(true)

      // Send only the content text to the API
      await documentService.editDocument(editingDocument, editorContent)

      // Close editor and refresh documents list
      setIsEditorOpen(false)
      setEditingDocument(null)
      setEditorContent('')
      setEditorDocumentName('')
      await loadDocuments()

      setAlertModal({
        isOpen: true,
        title: 'Success',
        message: 'Document saved successfully!',
        type: 'success'
      })
    } catch (error) {
      console.error('Error saving document:', error)
      setAlertModal({
        isOpen: true,
        title: 'Save Failed',
        message: error.message || 'Failed to save document. Please try again.',
        type: 'danger'
      })
    } finally {
      setIsSavingContent(false)
      setUploadProgress(0)
    }
  }

  // Close editor
  const handleCloseEditor = () => {
    if (isSavingContent) return // Prevent closing while saving

    if (editorContent.trim()) {
      setConfirmationModal({
        isOpen: true,
        title: 'Unsaved Changes',
        message: 'You have unsaved changes. Are you sure you want to close?',
        type: 'warning',
        confirmText: 'Close',
        cancelText: 'Cancel',
        onConfirm: () => {
          setIsEditorOpen(false)
          setEditingDocument(null)
          setEditorContent('')
          setEditorDocumentName('')
        }
      })
    } else {
      setIsEditorOpen(false)
      setEditingDocument(null)
      setEditorContent('')
      setEditorDocumentName('')
    }
  }

  const colorPresets = [
    { name: 'Blue', value: '#3b82f6' },
    { name: 'Green', value: '#10b981' },
    { name: 'Purple', value: '#8b5cf6' },
    { name: 'Pink', value: '#ec4899' },
    { name: 'Orange', value: '#f59e0b' },
    { name: 'Red', value: '#ef4444' },
    { name: 'Indigo', value: '#6366f1' },
    { name: 'Teal', value: '#14b8a6' }
  ]

  const positionOptions = [
    { value: 'bottom-right', label: 'Bottom Right' },
    { value: 'bottom-left', label: 'Bottom Left' },
    { value: 'top-right', label: 'Top Right' },
    { value: 'top-left', label: 'Top Left' }
  ]

  const themeOptions = [
    { value: 'dark', label: 'Dark' },
    { value: 'light', label: 'Light' },
    { value: 'black', label: 'Black' }
  ]

  const styleOptions = [
    { value: 'solid', label: 'Solid' },
    { value: 'glass', label: 'Glass Frost Effect' }
  ]

  const llmProviderOptions = [
    { value: 'openai', label: 'OpenAI' },
    { value: 'gemini', label: 'Gemini' }
    // { value: 'anthropic', label: 'Anthropic' },
    // { value: 'cohere', label: 'Cohere' },
    // { value: 'huggingface', label: 'Hugging Face' }
  ]

  const modelOptions = {
    openai: [
      { value: 'gpt-4', label: 'GPT-4' },
      { value: 'gpt-3.5-turbo', label: 'GPT-3.5 Turbo' },
      { value: 'gpt-4-turbo', label: 'GPT-4 Turbo' }
    ],
    google: [
      { value: 'gemini-2.5-flash-lite', label: 'Gemini 2.5 Flash Lite' },
      { value: 'gemini-pro', label: 'Gemini Pro' },
      { value: 'gemini-1.5-pro', label: 'Gemini 1.5 Pro' }
    ]
    // anthropic: [
    //   { value: 'claude-3-opus', label: 'Claude 3 Opus' },
    //   { value: 'claude-3-sonnet', label: 'Claude 3 Sonnet' },
    //   { value: 'claude-3-haiku', label: 'Claude 3 Haiku' }
    // ],
    // cohere: [
    //   { value: 'command', label: 'Command' },
    //   { value: 'command-light', label: 'Command Light' }
    // ],
    // huggingface: [
    //   { value: 'meta-llama/Llama-2-7b-chat-hf', label: 'Llama 2 7B' },
    //   { value: 'microsoft/DialoGPT-medium', label: 'DialoGPT Medium' }
    // ]
  }

  return (
    <div className="space-y-6">
      {/* Tab Navigation */}
      <div className="flex space-x-1 bg-secondary-800 p-1 rounded-lg">
        {tabs.map((tab) => {
          const Icon = tab.icon
          // Disable Appearance, Behavior, and Messages tabs if no model exists
          const isDisabled = !hasModel && (tab.id === 'appearance' || tab.id === 'behavior' || tab.id === 'messages')
          return (
            <button
              key={tab.id}
              onClick={() => !isDisabled && setActiveTab(tab.id)}
              disabled={isDisabled}
              className={`flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${isDisabled
                ? 'opacity-50 cursor-not-allowed text-secondary-500'
                : activeTab === tab.id
                  ? 'bg-primary-500 text-white'
                  : 'text-secondary-300 hover:text-white hover:bg-secondary-700'
                }`}
            >
              <Icon className="w-4 h-4" />
              <span>{tab.label}</span>
            </button>
          )
        })}
      </div>

      {/* Tab Content */}
      <div className="space-y-6">
        {activeTab === 'appearance' && (
          <div className="space-y-6">
            {/* Position */}
            <div>
              <label className="block text-sm font-medium text-white mb-2">
                Widget Position
              </label>
              <div className="grid grid-cols-2 gap-2">
                {positionOptions.map((option) => (
                  <button
                    key={option.value}
                    onClick={() => handleConfigChange('position', option.value)}
                    className={`p-3 rounded-lg border text-sm font-medium transition-colors ${config.position === option.value
                      ? 'border-primary-500 bg-primary-500/20 text-primary-400'
                      : 'border-secondary-600 bg-secondary-800 text-secondary-300 hover:border-secondary-500'
                      }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Theme */}
            <div>
              <label className="block text-sm font-medium text-white mb-2">
                Theme
              </label>
              <div className="flex space-x-3">
                {themeOptions.map((option) => (
                  <button
                    key={option.value}
                    onClick={() => handleConfigChange('theme', option.value)}
                    className={`flex items-center space-x-2 px-4 py-2 rounded-lg border transition-colors ${config.theme === option.value
                      ? 'border-primary-500 bg-primary-500/20 text-primary-400'
                      : 'border-secondary-600 bg-secondary-800 text-secondary-300 hover:border-secondary-500'
                      }`}
                  >
                    <Monitor className="w-4 h-4" />
                    <span>{option.label}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Style Effect */}
            <div>
              <label className="block text-sm font-medium text-white mb-2">
                Style Effect
              </label>
              <div className="flex space-x-3">
                {styleOptions.map((option) => (
                  <button
                    key={option.value}
                    onClick={() => handleConfigChange('themeStyle', option.value)}
                    className={`flex items-center space-x-2 px-4 py-2 rounded-lg border transition-colors ${(config.themeStyle || 'solid') === option.value
                      ? 'border-primary-500 bg-primary-500/20 text-primary-400'
                      : 'border-secondary-600 bg-secondary-800 text-secondary-300 hover:border-secondary-500'
                      }`}
                  >
                    {option.value === 'glass' ? (
                      <Sparkles className="w-4 h-4" />
                    ) : (
                      <Square className="w-4 h-4" />
                    )}
                    <span>{option.label}</span>
                  </button>
                ))}
              </div>
              <p className="text-xs text-secondary-400 mt-2">
                Choose between solid background or glassmorphic frost effect
              </p>
            </div>

            {/* Primary Color */}
            <div>
              <label className="block text-sm font-medium text-white mb-3">
                Primary Color
              </label>

              {/* Color Presets */}
              <div className="grid grid-cols-4 gap-2 mb-4">
                {colorPresets.map((preset) => (
                  <button
                    key={preset.value}
                    onClick={() => handleConfigChange('primaryColor', preset.value)}
                    className={`p-2 rounded-lg border-2 transition-all ${config.primaryColor === preset.value
                      ? 'border-white scale-110'
                      : 'border-secondary-600 hover:border-secondary-500'
                      }`}
                    style={{ backgroundColor: preset.value }}
                    title={preset.name}
                  />
                ))}
              </div>

              {/* Custom Color Input */}
              <div className="flex items-center space-x-3">
                <input
                  type="color"
                  value={config.primaryColor}
                  onChange={(e) => handleConfigChange('primaryColor', e.target.value)}
                  className="w-12 h-10 rounded-lg border border-secondary-600 bg-secondary-800 cursor-pointer"
                />
                <input
                  type="text"
                  value={config.primaryColor}
                  onChange={(e) => handleConfigChange('primaryColor', e.target.value)}
                  className="flex-1 px-3 py-2 bg-secondary-800 border border-secondary-600 rounded-lg text-white placeholder-secondary-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
                  placeholder="#3b82f6"
                />
              </div>
            </div>

            {/* Chatbot Avatar */}
            <div>
              <label className="block text-sm font-medium text-white mb-3">
                Chatbot Avatar
              </label>

              {/* Avatar Options */}
              <div className="grid grid-cols-4 gap-3 mb-4">
                {[
                  { id: 'bot-1', emoji: '🤖', name: 'Robot' },
                  { id: 'bot-2', emoji: '👨‍💼', name: 'Professional' },
                  { id: 'bot-3', emoji: '👩‍💻', name: 'Developer' },
                  { id: 'bot-4', emoji: '🎭', name: 'Friendly' },
                  { id: 'bot-5', emoji: '🧠', name: 'AI Brain' },
                  { id: 'bot-6', emoji: '💬', name: 'Chat' },
                  { id: 'bot-7', emoji: '⭐', name: 'Star' },
                  { id: 'bot-8', emoji: '🚀', name: 'Rocket' }
                ].map((avatar) => (
                  <button
                    key={avatar.id}
                    onClick={() => handleConfigChange('chatbotAvatar', avatar.emoji)}
                    className={`p-4 rounded-lg border-2 transition-all hover:scale-105 ${config.chatbotAvatar === avatar.emoji
                      ? 'border-primary-500 bg-primary-500/20'
                      : 'border-secondary-600 bg-secondary-800 hover:border-secondary-500'
                      }`}
                  >
                    <div className="text-2xl mb-1">{avatar.emoji}</div>
                    <div className="text-xs text-secondary-400">{avatar.name}</div>
                  </button>
                ))}
              </div>

              {/* Custom Avatar Upload */}
              <div className="border-2 border-dashed border-secondary-600 rounded-lg p-4 text-center hover:border-primary-500 transition-colors">
                <input
                  type="file"
                  id="avatar-upload"
                  accept="image/*"
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (file) {
                      const reader = new FileReader();
                      reader.onload = (event) => {
                        handleConfigChange('customAvatar', event.target.result);
                      };
                      reader.readAsDataURL(file);
                    }
                  }}
                  className="hidden"
                />
                <label
                  htmlFor="avatar-upload"
                  className="cursor-pointer flex flex-col items-center space-y-2"
                >
                  <div className="w-8 h-8 bg-primary-500/20 rounded-lg flex items-center justify-center">
                    <svg className="w-4 h-4 text-primary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                    </svg>
                  </div>
                  <p className="text-white text-sm">Upload Custom Avatar</p>
                </label>
              </div>

              {/* Current Avatar Display */}
              {((config.chatbotAvatar && config.chatbotAvatar !== '') || (config.customAvatar && config.customAvatar !== '')) && (
                <div className="mt-3 p-3 bg-secondary-700 rounded-lg">
                  <p className="text-white text-sm mb-2">Current Avatar:</p>
                  <div className="flex items-center space-x-3">
                    <div className="w-12 h-12 rounded-full bg-secondary-600 flex items-center justify-center text-2xl">
                      {config.customAvatar ? (
                        <img src={config.customAvatar} alt="Custom Avatar" className="w-full h-full rounded-full object-cover" />
                      ) : (
                        config.chatbotAvatar
                      )}
                    </div>
                    <div>
                      <p className="text-white text-sm">
                        {config.customAvatar ? 'Custom Avatar' : 'Selected Avatar'}
                      </p>
                      <button
                        onClick={() => {
                          handleConfigChange('chatbotAvatar', '');
                          handleConfigChange('customAvatar', '');
                        }}
                        className="text-red-400 text-xs hover:text-red-300"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Z-Index */}
            <div>
              <label className="block text-sm font-medium text-white mb-2">
                Z-Index
              </label>
              <input
                type="number"
                value={config.zIndex}
                onChange={(e) => handleConfigChange('zIndex', parseInt(e.target.value))}
                className="w-full px-3 py-2 bg-secondary-800 border border-secondary-600 rounded-lg text-white placeholder-secondary-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
                placeholder="9999"
                min="1"
                max="99999"
              />
              <p className="text-xs text-secondary-400 mt-1">
                Higher values appear on top of other elements
              </p>
            </div>
          </div>
        )}

        {activeTab === 'behavior' && (
          <div className="space-y-6">
            {/* Auto Open */}
            <div className="flex items-center justify-between">
              <div>
                <label className="block text-sm font-medium text-white">
                  Auto Open
                </label>
                <p className="text-xs text-secondary-400">
                  Automatically open the chat widget when the page loads
                </p>
              </div>
              <button
                type="button"
                onClick={() => handleConfigChange('autoOpen', !config.autoOpen)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 focus:ring-offset-secondary-800 ${config.autoOpen ? 'bg-primary-500' : 'bg-secondary-600'
                  }`}
                role="switch"
                aria-checked={config.autoOpen}
                aria-labelledby="auto-open-label"
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${config.autoOpen ? 'translate-x-6' : 'translate-x-1'
                    }`}
                />
              </button>
            </div>

            {/* Show Welcome Message */}
            <div className="flex items-center justify-between">
              <div>
                <label className="block text-sm font-medium text-white">
                  Show Welcome Message
                </label>
                <p className="text-xs text-secondary-400">
                  Display a welcome message when the chat opens
                </p>
              </div>
              <button
                onClick={() => handleConfigChange('showWelcomeMessage', !config.showWelcomeMessage)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${config.showWelcomeMessage ? 'bg-primary-500' : 'bg-secondary-600'
                  }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${config.showWelcomeMessage ? 'translate-x-6' : 'translate-x-1'
                    }`}
                />
              </button>
            </div>

            {/* Position */}
            <div>
              <label className="block text-sm font-medium text-white mb-2">
                Widget Position
              </label>
              <select
                value={config.position}
                onChange={(e) => handleConfigChange('position', e.target.value)}
                className="w-full px-3 py-2 bg-secondary-800 border border-secondary-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                {positionOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}

        {activeTab === 'messages' && (
          <div className="space-y-6">
            {/* Greeting Message */}
            <div>
              <label className="block text-sm font-medium text-white mb-2">
                Greeting Message
              </label>
              <textarea
                value={config.greetingMsg || 'Hello! How can I help you today?'}
                onChange={(e) => handleConfigChange('greetingMsg', e.target.value)}
                className="w-full px-3 py-2 bg-secondary-800 border border-secondary-600 rounded-lg text-white placeholder-secondary-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
                rows="3"
                placeholder="Hello! How can I help you today?"
              />
              <p className="text-xs text-secondary-400 mt-1">
                This message will be shown when the chat first opens
              </p>
            </div>

            {/* Bye Message */}
            <div>
              <label className="block text-sm font-medium text-white mb-2">
                Goodbye Message
              </label>
              <textarea
                value={config.byeMsg || 'Bye, have a great day!'}
                onChange={(e) => handleConfigChange('byeMsg', e.target.value)}
                className="w-full px-3 py-2 bg-secondary-800 border border-secondary-600 rounded-lg text-white placeholder-secondary-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
                rows="2"
                placeholder="Bye, have a great day!"
              />
              <p className="text-xs text-secondary-400 mt-1">
                This message will be shown when the user ends the conversation
              </p>
            </div>

            {/* Not Found Info Message */}
            <div>
              <label className="block text-sm font-medium text-white mb-2">
                Not Found Information Message
              </label>
              <textarea
                value={config.notFoundInfo || 'I\'m sorry, I don\'t have that information at the moment.'}
                onChange={(e) => handleConfigChange('notFoundInfo', e.target.value)}
                className="w-full px-3 py-2 bg-secondary-800 border border-secondary-600 rounded-lg text-white placeholder-secondary-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
                rows="2"
                placeholder="I'm sorry, I don't have that information at the moment."
              />
              <p className="text-xs text-secondary-400 mt-1">
                This message will be shown when the bot doesn't have information about a query
              </p>
            </div>

            {/* Placeholder Text */}
            <div>
              <label className="block text-sm font-medium text-white mb-2">
                Input Placeholder
              </label>
              <input
                type="text"
                value={config.placeholder}
                onChange={(e) => handleConfigChange('placeholder', e.target.value)}
                className="w-full px-3 py-2 bg-secondary-800 border border-secondary-600 rounded-lg text-white placeholder-secondary-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
                placeholder="Type your message..."
              />
            </div>

          </div>
        )}

        {activeTab === 'bot-config' && (
          <div className="space-y-6">
            {/* Company Information */}
            <div className="space-y-4">
              <h4 className="text-sm font-medium text-white">Company Information</h4>

              <div>
                <label className="block text-sm font-medium text-white mb-2">
                  Company Name
                </label>
                <input
                  type="text"
                  value={config.companyName || ''}
                  onChange={(e) => handleConfigChange('companyName', e.target.value)}
                  className="w-full px-3 py-2 bg-secondary-800 border border-secondary-600 rounded-lg text-white placeholder-secondary-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
                  placeholder="Talkida.co.in"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-white mb-2">
                  Bot Aim
                </label>
                <textarea
                  value={config.botAim || ''}
                  onChange={(e) => handleConfigChange('botAim', e.target.value)}
                  className="w-full px-3 py-2 bg-secondary-800 border border-secondary-600 rounded-lg text-white placeholder-secondary-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
                  rows="3"
                  placeholder="Describe the main purpose and goals of your bot..."
                />
                <p className="text-xs text-secondary-400 mt-1">
                  This helps the bot understand its role and provide more relevant responses
                </p>
              </div>
            </div>

            {/* LLM Configuration */}
            <div className="space-y-4">
              <h4 className="text-sm font-medium text-white">LLM Configuration</h4>

              <div>
                <label className="block text-sm font-medium text-white mb-2">
                  LLM Provider
                </label>
                <select
                  value={config.llmProvider || 'google'}
                  onChange={(e) => handleConfigChange('llmProvider', e.target.value)}
                  className="w-full px-3 py-2 bg-secondary-800 border border-secondary-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  {llmProviderOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-white mb-2">
                  Model Name
                </label>
                <select
                  value={config.modelName || 'gemini-2.5-flash-lite'}
                  onChange={(e) => handleConfigChange('modelName', e.target.value)}
                  className="w-full px-3 py-2 bg-secondary-800 border border-secondary-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  {(modelOptions[config.llmProvider] || modelOptions.google).map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-white mb-2">
                  LLM API Key
                </label>
                <div className="relative">
                  <input
                    type={showApiKey ? "text" : "password"}
                    value={config.apiKeyEncrypted || ''}
                    onChange={(e) => handleConfigChange('apiKeyEncrypted', e.target.value)}
                    className="w-full px-3 py-2 pr-10 bg-secondary-800 border border-secondary-600 rounded-lg text-white placeholder-secondary-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="Your LLM API key (e.g., OpenAI, Google AI)"
                  />
                  <button
                    type="button"
                    onClick={() => setShowApiKey(!showApiKey)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-secondary-400 hover:text-white transition-colors"
                  >
                    {showApiKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <p className="text-xs text-secondary-400 mt-1">
                  Your API key will be securely stored and used for LLM requests
                </p>
              </div>
            </div>

            {/* Model Parameters */}
            <div className="space-y-4">
              <h4 className="text-sm font-medium text-white">Model Parameters</h4>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-white mb-2">
                    Max Tokens
                  </label>
                  <input
                    type="number"
                    value={config.maxTokens || 150}
                    onChange={(e) => handleConfigChange('maxTokens', parseInt(e.target.value))}
                    className="w-full px-3 py-2 bg-secondary-800 border border-secondary-600 rounded-lg text-white placeholder-secondary-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
                    min="1"
                    max="4096"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-white mb-2">
                    Temperature
                  </label>

                  {/* Temperature Slider */}
                  <div className="mb-3">
                    <input
                      type="range"
                      min="0"
                      max="2"
                      step="0.1"
                      value={config.temperature || 0.0}
                      onChange={(e) => handleConfigChange('temperature', parseFloat(e.target.value))}
                      className="w-full h-2 bg-secondary-700 rounded-lg appearance-none cursor-pointer slider"
                      style={{
                        background: `linear-gradient(to right, #3b82f6 0%, #3b82f6 ${((config.temperature || 0.0) / 2) * 100}%, #374151 ${((config.temperature || 0.0) / 2) * 100}%, #374151 100%)`
                      }}
                    />
                    <div className="flex justify-between text-xs text-secondary-400 mt-1">
                      <span>0.0 (Deterministic)</span>
                      <span className="font-medium text-white">{config.temperature || 0.0}</span>
                      <span>2.0 (Very Creative)</span>
                    </div>
                  </div>

                  {/* Temperature Input Field */}
                  <input
                    type="number"
                    step="0.1"
                    min="0"
                    max="2"
                    value={config.temperature || 0.0}
                    onChange={(e) => handleConfigChange('temperature', parseFloat(e.target.value))}
                    className="w-full px-3 py-2 bg-secondary-800 border border-secondary-600 rounded-lg text-white placeholder-secondary-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                  <p className="text-xs text-secondary-400 mt-1">
                    0.0 = deterministic, 2.0 = very creative
                  </p>
                </div>
              </div>
            </div>

            {/* Document Upload */}
            <div className={hasModel ? '' : 'opacity-50 pointer-events-none'}>
              <label className="block text-sm font-medium text-white mb-2">
                Upload Knowledge Document
                {!hasModel && (
                  <span className="ml-2 text-xs text-secondary-400">(Please save bot configuration first)</span>
                )}
              </label>

              {/* Upload Area */}
              <div className={`border-2 border-dashed border-secondary-600 rounded-lg p-6 text-center transition-colors ${hasModel ? 'hover:border-primary-500' : 'cursor-not-allowed'}`}>
                <input
                  type="file"
                  id="document-upload"
                  accept=".pdf,.txt"
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (file && hasModel) {
                      handleFileUpload(file);
                    }
                  }}
                  disabled={isUploading || !hasModel}
                  className="hidden"
                />
                <label
                  htmlFor="document-upload"
                  className={`flex flex-col items-center space-y-2 ${isUploading || !hasModel ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                >
                  <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${hasModel ? 'bg-primary-500/20' : 'bg-secondary-700'}`}>
                    <Upload className={`w-6 h-6 ${hasModel ? 'text-primary-400' : 'text-secondary-500'}`} />
                  </div>
                  <div>
                    <p className="text-white font-medium">
                      {isUploading ? 'Uploading...' : 'Click to upload document'}
                    </p>
                    <p className="text-secondary-400 text-sm">PDF, TXT files supported</p>
                  </div>
                </label>

                {/* Upload Progress */}
                {isUploading && (
                  <div className="mt-4">
                    <div className="w-full bg-secondary-700 rounded-full h-2">
                      <div
                        className="bg-primary-500 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${uploadProgress}%` }}
                      ></div>
                    </div>
                    <p className="text-secondary-400 text-sm mt-2">{uploadProgress}% uploaded</p>
                  </div>
                )}
              </div>

              {/* Documents List */}
              <div className="mt-4">
                <div className="flex items-center justify-between mb-2">
                  <h5 className="text-sm font-medium text-white">Uploaded Documents</h5>
                  <button
                    onClick={loadDocuments}
                    disabled={isLoadingDocuments || !hasModel}
                    className={`text-sm ${hasModel ? 'text-primary-400 hover:text-primary-300' : 'text-secondary-500 cursor-not-allowed'}`}
                  >
                    {isLoadingDocuments ? 'Loading...' : 'Refresh'}
                  </button>
                </div>

                {documents.length > 0 ? (
                  <div className="space-y-2">
                    {documents.map((doc) => (
                      <div key={doc.id} className="flex items-center justify-between p-3 bg-secondary-700 rounded-lg">
                        <div className="flex items-center space-x-3">
                          <FileText className="w-5 h-5 text-primary-400" />
                          <div>
                            <p className="text-white text-sm font-medium">{doc.document_name || doc.name || doc.filename}</p>
                            <p className="text-secondary-400 text-xs">
                              ID: {doc.id}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => hasModel && handleEditDocument(doc.id)}
                            disabled={!hasModel}
                            className={`p-1 ${hasModel ? 'text-primary-400 hover:text-primary-300' : 'text-secondary-500 cursor-not-allowed'}`}
                            title={hasModel ? 'Edit document' : 'Please save bot configuration first'}
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => hasModel && handleDeleteDocument(doc.id)}
                            disabled={!hasModel}
                            className={`p-1 ${hasModel ? 'text-red-400 hover:text-red-300' : 'text-secondary-500 cursor-not-allowed'}`}
                            title={hasModel ? 'Delete document' : 'Please save bot configuration first'}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-4 text-secondary-400 text-sm">
                    No documents uploaded yet
                  </div>
                )}
              </div>

              <p className="text-xs text-secondary-400 mt-2">
                Upload documents to provide context and knowledge to your chatbot
              </p>
            </div>

            {/* Bot Identity */}
            <div className="space-y-4">
              <h4 className="text-sm font-medium text-white">Bot Identity</h4>

              <div>
                <label className="block text-sm font-medium text-white mb-2">
                  Bot Name
                </label>
                <input
                  type="text"
                  value={config.botName || 'Talkida Technologies'}
                  onChange={(e) => handleConfigChange('botName', e.target.value)}
                  className="w-full px-3 py-2 bg-secondary-800 border border-secondary-600 rounded-lg text-white placeholder-secondary-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
                  placeholder="Talkida Technologies"
                />
              </div>

              {/* Bot UUID and Secret Key Display */}
              {(config.botUuid || config.botSecretKey) && (
                <div className="space-y-3">
                  <h5 className="text-sm font-medium text-white">Bot Credentials</h5>

                  {config.botUuid && (
                    <div>
                      <label className="block text-xs font-medium text-secondary-400 mb-1">
                        Bot UUID
                      </label>
                      <div className="flex items-center space-x-2">
                        <input
                          type="text"
                          value={config.botUuid}
                          readOnly
                          className="flex-1 px-3 py-2 bg-secondary-900 border border-secondary-700 rounded-lg text-secondary-300 text-sm font-mono"
                        />
                        <button
                          onClick={() => navigator.clipboard.writeText(config.botUuid)}
                          className="px-3 py-2 bg-secondary-700 hover:bg-secondary-600 text-white text-sm rounded-lg transition-colors"
                        >
                          Copy
                        </button>
                      </div>
                    </div>
                  )}

                  {config.botSecretKey && (
                    <div>
                      <label className="block text-xs font-medium text-secondary-400 mb-1">
                        Bot Secret Key
                      </label>
                      <div className="flex items-center space-x-2">
                        <input
                          type="password"
                          value={config.botSecretKey}
                          readOnly
                          className="flex-1 px-3 py-2 bg-secondary-900 border border-secondary-700 rounded-lg text-secondary-300 text-sm font-mono"
                        />
                        <button
                          onClick={() => navigator.clipboard.writeText(config.botSecretKey)}
                          className="px-3 py-2 bg-secondary-700 hover:bg-secondary-600 text-white text-sm rounded-lg transition-colors"
                        >
                          Copy
                        </button>
                      </div>
                    </div>
                  )}

                  <p className="text-xs text-secondary-500">
                    These credentials are used for widget integration. Keep them secure.
                  </p>

                  {/* Regenerate Button */}
                  <div className="pt-3 border-t border-secondary-700">
                    <button
                      onClick={() => setRegenerateModal({ isOpen: true })}
                      disabled={isGeneratingCredentials || !hasModel}
                      className="w-full px-4 py-2 bg-orange-500 hover:bg-orange-600 disabled:bg-orange-500/50 disabled:cursor-not-allowed text-white text-sm font-medium rounded-lg transition-colors flex items-center justify-center space-x-2"
                    >
                      {isGeneratingCredentials ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          <span>Regenerating...</span>
                        </>
                      ) : (
                        <>
                          <RefreshCw className="w-4 h-4" />
                          <span>Regenerate Bot Credentials</span>
                        </>
                      )}
                    </button>
                    <p className="text-xs text-secondary-500 mt-2">
                      Warning: Regenerating will create new credentials. Old credentials will no longer work.
                    </p>
                  </div>
                </div>
              )}

              {/* Generate Bot Credentials Button - Show when credentials are missing */}
              {(!config.botUuid || !config.botSecretKey) && (
                <div className="space-y-3">
                  <h5 className="text-sm font-medium text-white">Bot Credentials</h5>
                  <div className="p-4 bg-secondary-700/50 border border-secondary-600 rounded-lg">
                    <div className="flex items-center space-x-3 mb-3">
                      <Key className={`w-5 h-5 ${hasModel ? 'text-yellow-400' : 'text-secondary-500'}`} />
                      <div>
                        <p className="text-sm font-medium text-white">
                          Bot credentials not generated
                        </p>
                        <p className="text-xs text-secondary-400 mt-1">
                          {hasModel
                            ? 'Generate bot credentials to enable widget integration'
                            : 'Please complete and save bot configuration to generate credentials'}
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={handleGenerateCredentials}
                      disabled={isGeneratingCredentials || !hasModel}
                      className="w-full px-4 py-2 bg-primary-500 hover:bg-primary-600 disabled:bg-primary-500/50 disabled:cursor-not-allowed text-white text-sm font-medium rounded-lg transition-colors flex items-center justify-center space-x-2"
                    >
                      {isGeneratingCredentials ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          <span>Generating...</span>
                        </>
                      ) : (
                        <>
                          <Key className="w-4 h-4" />
                          <span>Generate Bot Credentials</span>
                        </>
                      )}
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Custom System Prompt */}
            <div>
              <label className="block text-sm font-medium text-white mb-2">
                Custom System Prompt
              </label>
              <textarea
                value={config.customSystPrompt || ''}
                onChange={(e) => handleConfigChange('customSystPrompt', e.target.value)}
                className="w-full px-3 py-2 bg-secondary-800 border border-secondary-600 rounded-lg text-white placeholder-secondary-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
                rows="4"
                placeholder="You are a helpful assistant for [Company Name]. Your role is to..."
              />
              <p className="text-xs text-secondary-400 mt-1">
                Define the bot's behavior, personality, and knowledge boundaries
              </p>
            </div>

            {/* Lead Collection Settings */}
            <div className="space-y-4">
              <h4 className="text-sm font-medium text-white">Lead Collection</h4>

              <div className="flex items-center justify-between">
                <div>
                  <label className="block text-sm font-medium text-white">
                    Enable Lead Collection
                  </label>
                  <p className="text-xs text-secondary-400">
                    Collect visitor information before starting the conversation
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => handleConfigChange('enableLeadCollection', !config.enableLeadCollection)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 focus:ring-offset-secondary-800 ${config.enableLeadCollection ? 'bg-primary-500' : 'bg-secondary-600'
                    }`}
                  role="switch"
                  aria-checked={config.enableLeadCollection}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${config.enableLeadCollection ? 'translate-x-6' : 'translate-x-1'
                      }`}
                  />
                </button>
              </div>

              {config.enableLeadCollection && (
                <div>
                  <label className="block text-sm font-medium text-white mb-2">
                    Lead Collection Fields
                  </label>

                  {/* Predefined Fields */}
                  <div className="space-y-2 mb-4">
                    {predefinedFields.map((field) => (
                      <label key={field.key} className="flex items-center space-x-3">
                        <input
                          type="checkbox"
                          checked={config.leadFields ? config.leadFields.split(',').includes(field.key) : field.required}
                          onChange={(e) => {
                            const currentFields = config.leadFields ? config.leadFields.split(',').filter(f => f.trim()) : []
                            const customFieldKeys = customFields.map(f => f.key)
                            let newFields
                            if (e.target.checked) {
                              newFields = [...currentFields.filter(f => !customFieldKeys.includes(f.trim())), field.key, ...customFieldKeys]
                            } else {
                              newFields = currentFields.filter(f => f.trim() !== field.key && !customFieldKeys.includes(f.trim())).concat(customFieldKeys)
                            }
                            handleConfigChange('leadFields', newFields.join(','))
                          }}
                          disabled={field.required}
                          className="w-4 h-4 text-primary-500 bg-secondary-800 border-secondary-600 rounded focus:ring-primary-500 focus:ring-2"
                        />
                        <span className="text-white text-sm">
                          {field.label}
                          {field.required && <span className="text-red-400 ml-1">*</span>}
                        </span>
                      </label>
                    ))}
                  </div>

                  {/* Custom Fields */}
                  {customFields.length > 0 && (
                    <div className="space-y-2 mb-4">
                      <label className="block text-xs font-medium text-secondary-400 mb-2">
                        Custom Fields
                      </label>
                      {customFields.map((field) => (
                        <div key={field.key} className="flex items-center justify-between bg-secondary-900/50 p-2 rounded">
                          <label className="flex items-center space-x-3 flex-1">
                            <input
                              type="checkbox"
                              checked={config.leadFields ? config.leadFields.split(',').includes(field.key) : false}
                              onChange={(e) => {
                                const currentFields = config.leadFields ? config.leadFields.split(',').filter(f => f.trim()) : []
                                const predefinedKeys = predefinedFields.map(f => f.key)
                                const otherCustomFields = customFields.filter(f => f.key !== field.key).map(f => f.key)

                                let newFields
                                if (e.target.checked) {
                                  newFields = [
                                    ...currentFields.filter(f => predefinedKeys.includes(f.trim())),
                                    field.key,
                                    ...otherCustomFields.filter(k => currentFields.includes(k))
                                  ]
                                } else {
                                  newFields = currentFields.filter(f => f.trim() !== field.key)
                                }
                                handleConfigChange('leadFields', newFields.join(','))
                              }}
                              className="w-4 h-4 text-primary-500 bg-secondary-800 border-secondary-600 rounded focus:ring-primary-500 focus:ring-2"
                            />
                            <span className="text-white text-sm">{field.label}</span>
                          </label>
                          <button
                            onClick={() => {
                              const currentFields = config.leadFields ? config.leadFields.split(',').filter(f => f.trim() && f.trim() !== field.key) : []
                              const newCustomFields = customFields.filter(f => f.key !== field.key)
                              setCustomFields(newCustomFields)
                              handleConfigChange('leadFields', currentFields.join(','))
                            }}
                            className="text-red-400 hover:text-red-300 p-1 transition-colors"
                            title="Remove custom field"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Add Custom Field */}
                  <div className="mt-4 pt-4 border-t border-secondary-700">
                    <label className="block text-sm font-medium text-white mb-2">
                      Add Custom Field
                    </label>
                    <div className="flex items-center space-x-2">
                      <input
                        type="text"
                        value={customFieldInput}
                        onChange={(e) => setCustomFieldInput(e.target.value)}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            handleAddCustomField()
                          }
                        }}
                        placeholder="e.g., budget_range, project_timeline"
                        className="flex-1 px-3 py-2 bg-secondary-800 border border-secondary-600 rounded-lg text-white placeholder-secondary-400 focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm"
                      />
                      <button
                        onClick={handleAddCustomField}
                        disabled={!customFieldInput.trim()}
                        className="px-4 py-2 bg-primary-500 hover:bg-primary-600 disabled:bg-primary-500/50 disabled:cursor-not-allowed text-white rounded-lg transition-colors flex items-center space-x-2"
                        title="Add custom field"
                      >
                        <Plus className="w-4 h-4" />
                        <span className="text-sm">Add</span>
                      </button>
                    </div>
                    <p className="text-xs text-secondary-400 mt-2">
                      Enter a field name (use underscores for spaces, e.g., "budget_range")
                    </p>
                  </div>

                  <p className="text-xs text-secondary-400 mt-4">
                    Required fields (*) cannot be disabled. Visitors must provide this information before chatting.
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

      </div>

      {/* Document Editor Modal */}
      {isEditorOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-secondary-800 rounded-lg w-full max-w-4xl max-h-[90vh] flex flex-col">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-4 border-b border-secondary-700">
              <div>
                <h3 className="text-lg font-semibold text-white">Edit Document</h3>
                <p className="text-sm text-secondary-400 mt-1">{editorDocumentName}</p>
              </div>
              <button
                onClick={handleCloseEditor}
                disabled={isSavingContent}
                className="text-secondary-400 hover:text-white p-1 disabled:opacity-50 disabled:cursor-not-allowed"
                title="Close editor"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="flex-1 overflow-hidden flex flex-col p-4">
              {isLoadingContent ? (
                <div className="flex items-center justify-center h-full">
                  <div className="flex items-center space-x-3 text-secondary-400">
                    <Loader2 className="w-6 h-6 animate-spin" />
                    <span>Loading document content...</span>
                  </div>
                </div>
              ) : (
                <>
                  <textarea
                    value={editorContent}
                    onChange={(e) => setEditorContent(e.target.value)}
                    className="w-full h-full min-h-[400px] px-4 py-3 bg-secondary-900 border border-secondary-600 rounded-lg text-white placeholder-secondary-400 focus:outline-none focus:ring-2 focus:ring-primary-500 font-mono text-sm resize-none"
                    placeholder="Document content will appear here..."
                    disabled={isSavingContent}
                  />

                  {/* Save Status */}
                  {isSavingContent && (
                    <div className="mt-4 flex items-center justify-center space-x-2 text-secondary-400">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span className="text-sm">Saving document...</span>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Modal Footer */}
            <div className="flex items-center justify-end space-x-3 p-4 border-t border-secondary-700">
              <button
                onClick={handleCloseEditor}
                disabled={isSavingContent}
                className="px-4 py-2 text-sm font-medium text-secondary-300 hover:text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveDocument}
                disabled={isSavingContent || isLoadingContent || !editorContent.trim()}
                className="px-6 py-2 bg-primary-500 hover:bg-primary-600 disabled:bg-primary-500/50 disabled:cursor-not-allowed text-white text-sm font-medium rounded-lg transition-colors flex items-center space-x-2"
              >
                {isSavingContent ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span>Saving...</span>
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    <span>Save Changes</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={confirmationModal.isOpen}
        onClose={() => setConfirmationModal({ ...confirmationModal, isOpen: false })}
        onConfirm={confirmationModal.onConfirm}
        title={confirmationModal.title}
        message={confirmationModal.message}
        type={confirmationModal.type}
        confirmText={confirmationModal.confirmText}
        cancelText={confirmationModal.cancelText}
      />

      {/* Regenerate Credentials Confirmation Modal */}
      <ConfirmationInputModal
        isOpen={regenerateModal.isOpen}
        onClose={() => setRegenerateModal({ isOpen: false })}
        onConfirm={handleRegenerateCredentials}
        title="Regenerate Bot Credentials"
        message="Are you sure you want to regenerate bot credentials? This will create new credentials and the old ones will no longer work. Make sure to update your widget integration code with the new credentials."
        type="danger"
        confirmText="Regenerate"
        cancelText="Cancel"
        confirmationText="Yes I Confirm"
      />

      {/* Alert Modal */}
      <AlertModal
        isOpen={alertModal.isOpen}
        onClose={() => setAlertModal({ ...alertModal, isOpen: false })}
        title={alertModal.title}
        message={alertModal.message}
        type={alertModal.type}
      />
    </div>
  )
}

export default WidgetConfig
